"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { Heart, Reply, Edit2, Trash2, Send } from "lucide-react"
import type { RootState } from "../../store/store"
import { commentService, type Comment } from "../../services/commentService"
import toast from "react-hot-toast"

interface CommentSectionProps {
  productId: string
}

const CommentSection: React.FC<CommentSectionProps> = ({ productId }) => {
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState("")
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyContent, setReplyContent] = useState("")
  const [editingComment, setEditingComment] = useState<string | null>(null)
  const [editContent, setEditContent] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { user } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    fetchComments()
  }, [productId])

  const fetchComments = async () => {
    try {
      const fetchedComments = await commentService.getComments(productId)
      setComments(fetchedComments)
    } catch (error) {
      console.error("Error fetching comments:", error)
      toast.error("Failed to load comments")
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddComment = async () => {
    if (!user) {
      toast.error("Please sign in to comment")
      return
    }

    if (!newComment.trim()) {
      toast.error("Please enter a comment")
      return
    }

    setIsSubmitting(true)
    try {
      await commentService.addComment(productId, user.uid, user.name, user.email, newComment.trim())
      setNewComment("")
      await fetchComments()
      toast.success("Comment added successfully!")
    } catch (error) {
      console.error("Error adding comment:", error)
      toast.error("Failed to add comment")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddReply = async (parentId: string) => {
    if (!user) {
      toast.error("Please sign in to reply")
      return
    }

    if (!replyContent.trim()) {
      toast.error("Please enter a reply")
      return
    }

    setIsSubmitting(true)
    try {
      await commentService.addComment(productId, user.uid, user.name, user.email, replyContent.trim(), parentId)
      setReplyContent("")
      setReplyingTo(null)
      await fetchComments()
      toast.success("Reply added successfully!")
    } catch (error) {
      console.error("Error adding reply:", error)
      toast.error("Failed to add reply")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLikeComment = async (commentId: string) => {
    if (!user) {
      toast.error("Please sign in to like comments")
      return
    }

    try {
      await commentService.toggleCommentLike(commentId, user.uid)
      await fetchComments()
    } catch (error) {
      console.error("Error liking comment:", error)
      toast.error("Failed to like comment")
    }
  }

  const handleEditComment = async (commentId: string) => {
    if (!editContent.trim()) {
      toast.error("Please enter comment content")
      return
    }

    try {
      await commentService.updateComment(commentId, editContent.trim())
      setEditingComment(null)
      setEditContent("")
      await fetchComments()
      toast.success("Comment updated successfully!")
    } catch (error) {
      console.error("Error updating comment:", error)
      toast.error("Failed to update comment")
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return
    }

    try {
      await commentService.deleteComment(commentId, productId)
      await fetchComments()
      toast.success("Comment deleted successfully!")
    } catch (error) {
      console.error("Error deleting comment:", error)
      toast.error("Failed to delete comment")
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 1) {
      return "Just now"
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)} days ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const CommentItem: React.FC<{ comment: Comment; isReply?: boolean }> = ({ comment, isReply = false }) => (
    <div className={`${isReply ? "ml-8 border-l-2 border-gray-200 pl-4" : ""} mb-4`}>
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
              {comment.userName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-900">{comment.userName}</p>
              <p className="text-xs text-gray-500">{formatDate(comment.createdAt)}</p>
            </div>
          </div>
          {user && user.uid === comment.userId && (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setEditingComment(comment.id)
                  setEditContent(comment.content)
                }}
                className="text-gray-400 hover:text-blue-600 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteComment(comment.id)}
                className="text-gray-400 hover:text-red-600 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {editingComment === comment.id ? (
          <div className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={3}
            />
            <div className="flex space-x-2">
              <button
                onClick={() => handleEditComment(comment.id)}
                className="px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 transition-colors"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditingComment(null)
                  setEditContent("")
                }}
                className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-gray-800 mb-3">{comment.content}</p>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleLikeComment(comment.id)}
                className={`flex items-center space-x-1 text-sm transition-colors ${
                  user && comment.likes.includes(user.uid) ? "text-red-500" : "text-gray-500 hover:text-red-500"
                }`}
              >
                <Heart className="w-4 h-4" fill={user && comment.likes.includes(user.uid) ? "currentColor" : "none"} />
                <span>{comment.likesCount}</span>
              </button>
              {!isReply && (
                <button
                  onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                  className="flex items-center space-x-1 text-sm text-gray-500 hover:text-primary-600 transition-colors"
                >
                  <Reply className="w-4 h-4" />
                  <span>Reply</span>
                </button>
              )}
            </div>
          </>
        )}

        {replyingTo === comment.id && (
          <div className="mt-3 space-y-2">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write a reply..."
              className="w-full p-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={2}
            />
            <div className="flex space-x-2">
              <button
                onClick={() => handleAddReply(comment.id)}
                disabled={isSubmitting}
                className="px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
              >
                <Send className="w-3 h-3" />
                <span>Reply</span>
              </button>
              <button
                onClick={() => {
                  setReplyingTo(null)
                  setReplyContent("")
                }}
                className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Render replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-4">
          {comment.replies.map((reply) => (
            <CommentItem key={reply.id} comment={reply} isReply={true} />
          ))}
        </div>
      )}
    </div>
  )

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">
        Comments ({comments.reduce((total, comment) => total + 1 + (comment.replies?.length || 0), 0)})
      </h3>

      {/* Add new comment */}
      <div className="space-y-3">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={user ? "Write a comment..." : "Please sign in to comment"}
          disabled={!user}
          className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
          rows={3}
        />
        <button
          onClick={handleAddComment}
          disabled={!user || isSubmitting || !newComment.trim()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Send className="w-4 h-4" />
          <span>{isSubmitting ? "Posting..." : "Post Comment"}</span>
        </button>
      </div>

      {/* Comments list */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No comments yet. Be the first to comment!</p>
        ) : (
          comments.map((comment) => <CommentItem key={comment.id} comment={comment} />)
        )}
      </div>
    </div>
  )
}

export default CommentSection
